import { useCallback, useEffect, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { Link, createFileRoute } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminContext } from "@/lib/admin-context";

/**
 * Admin → Overage events.
 *
 * Port of `src/app/admin/features/overages/overages-client.tsx` (legacy NextJS, ~305 LOC) per D21.
 *
 * Read-only ledger view: KPIs, top-features-by-revenue, recent events. Backed by
 * GET /api/admin/features/overages?since=&status=&featureKey= → { kpis, events, byFeature }.
 *
 * Per-cell overage CONFIG (price/unit, max/period, batch size, batch price) is edited from the
 * Features × Plans matrix at /features via the Overage editor dialog — there's no separate
 * settings PATCH endpoint, the legacy app reuses PUT /api/admin/features.
 */

interface OverageEvent {
  id: string;
  ownerId: string;
  ownerType: string;
  featureKey: string;
  kind: string;
  units: number;
  unitPriceCents: number;
  totalCents: number;
  currency: string;
  status: string;
  stripeInvoiceItemId: string | null;
  stripeInvoiceId: string | null;
  failureReason: string | null;
  createdAt: string;
  postedAt: string | null;
  paidAt: string | null;
}

interface ByFeature {
  featureKey: string;
  count: number;
  totalCents: number;
  totalUnits: number;
}

interface Kpis {
  totalCents: number;
  revenueCents: number;
  pendingCount: number;
  failedCount: number;
  eventCount: number;
  sinceDays: number;
}

const STATUS_STYLES: Record<string, string> = {
  pending: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  posted: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
  paid: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  failed: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",
  void: "bg-zinc-200 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
};

const euros = (cents: number) => `€${(cents / 100).toFixed(2)}`;

export const Route = createFileRoute("/_authenticated/features-overages")({
  component: OveragesPage,
});

function OveragesPage() {
  const { t } = useLingui();
  const { dbTarget } = useAdminContext();
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [events, setEvents] = useState<OverageEvent[]>([]);
  const [byFeature, setByFeature] = useState<ByFeature[]>([]);
  const [loading, setLoading] = useState(true);
  const [sinceDays, setSinceDays] = useState(30);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [featureFilter, setFeatureFilter] = useState<string>("");

  const fetchOverages = useCallback((): void => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("since", String(sinceDays));
    if (statusFilter) params.set("status", statusFilter);
    if (featureFilter) params.set("featureKey", featureFilter);

    apiFetch
      .get<{ kpis: Kpis | null; events: OverageEvent[]; byFeature: ByFeature[] }>(
        `/api/admin/features/overages?${params.toString()}`,
      )
      .then((data) => {
        setKpis(data.kpis ?? null);
        setEvents(data.events ?? []);
        setByFeature(data.byFeature ?? []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // dbTarget is intentionally part of the dep chain (via useEffect below) so
    // the manual refetch fires when the admin flips local↔prod.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sinceDays, statusFilter, featureFilter, dbTarget]);

  useEffect(() => {
    fetchOverages();
  }, [fetchOverages]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">
            <Trans>Overage events</Trans>
          </h1>
          <p className="text-muted-foreground text-sm">
            <Trans>
              Stripe invoice items auto-created when users exceed quota on overage-enabled features,
              plus explicit batch purchases.
            </Trans>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={fetchOverages}
            disabled={loading}
            aria-label={t`Refresh overage events`}
          >
            <Trans>Refresh</Trans>
          </Button>
          <Link to="/features">
            <Button variant="outline" size="sm">
              <Trans>← Matrix</Trans>
            </Button>
          </Link>
          <Link to="/features-analytics">
            <Button variant="outline" size="sm">
              <Trans>Analytics →</Trans>
            </Button>
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            onClick={() => setSinceDays(d)}
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              sinceDays === d
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
          >
            <Trans>Last {d} days</Trans>
          </button>
        ))}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-card border-border rounded-full border px-3 py-1 text-xs"
        >
          <option value="">{t`All statuses`}</option>
          <option value="pending">Pending</option>
          <option value="posted">Posted (Stripe)</option>
          <option value="paid">Paid</option>
          <option value="failed">Failed</option>
          <option value="void">Void</option>
        </select>
        {featureFilter && (
          <button
            onClick={() => setFeatureFilter("")}
            className="bg-muted text-muted-foreground hover:bg-muted/80 rounded-full px-3 py-1 text-xs"
          >
            ✕ Feature: {featureFilter}
          </button>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          {
            label: t`Revenue (posted + paid)`,
            value: kpis ? euros(kpis.revenueCents) : "—",
            icon: "💰",
            color: "text-emerald-600",
          },
          {
            label: t`Total invoiced`,
            value: kpis ? euros(kpis.totalCents) : "—",
            icon: "📊",
            color: "text-primary",
          },
          {
            label: t`Pending`,
            value: kpis ? kpis.pendingCount : "—",
            icon: "⏳",
            color: "text-amber-600",
          },
          {
            label: t`Failed`,
            value: kpis ? kpis.failedCount : "—",
            icon: "⚠️",
            color: "text-red-600",
          },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="p-4">
              <span className="text-2xl">{s.icon}</span>
              <div className={`mt-2 text-2xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-muted-foreground text-xs">{s.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            <Trans>Top features by revenue</Trans>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2 p-2">
              <Skeleton className="h-7 w-full" />
              <Skeleton className="h-7 w-full" />
              <Skeleton className="h-7 w-3/4" />
            </div>
          ) : byFeature.length === 0 ? (
            <div className="text-muted-foreground p-4 text-center text-sm">
              <Trans>No overages in range</Trans>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-border/50 border-b text-left">
                    <th className="px-3 py-2 font-medium">
                      <Trans>Feature</Trans>
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      <Trans>Events</Trans>
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      <Trans>Units</Trans>
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      <Trans>Total</Trans>
                    </th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {byFeature.map((row) => (
                    <tr
                      key={row.featureKey}
                      className="border-border/30 hover:bg-muted/20 border-b"
                    >
                      <td className="px-3 py-2 font-mono text-[0.6875rem]">{row.featureKey}</td>
                      <td className="px-3 py-2 text-right">{row.count}</td>
                      <td className="px-3 py-2 text-right">{row.totalUnits}</td>
                      <td className="px-3 py-2 text-right font-semibold">
                        {euros(row.totalCents)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => setFeatureFilter(row.featureKey)}
                          className="text-primary text-xs underline"
                        >
                          <Trans>Details</Trans>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            <Trans>Recent events</Trans>{" "}
            <span className="text-muted-foreground ml-2 text-xs font-normal">
              <Trans>(first {events.length})</Trans>
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2 p-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-7 w-full" />
              ))}
            </div>
          ) : events.length === 0 ? (
            <div className="text-muted-foreground p-4 text-center text-sm">
              <Trans>No events</Trans>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-border/50 border-b text-left">
                    <th className="px-3 py-2 font-medium">
                      <Trans>Date</Trans>
                    </th>
                    <th className="px-3 py-2 font-medium">
                      <Trans>Feature</Trans>
                    </th>
                    <th className="hidden px-3 py-2 font-medium sm:table-cell">
                      <Trans>Owner</Trans>
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      <Trans>Units</Trans>
                    </th>
                    <th className="hidden px-3 py-2 text-right font-medium md:table-cell">
                      <Trans>Unit px</Trans>
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      <Trans>Total</Trans>
                    </th>
                    <th className="px-3 py-2 font-medium">
                      <Trans>Status</Trans>
                    </th>
                    <th className="hidden px-3 py-2 font-medium lg:table-cell">Stripe</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((e) => (
                    <tr key={e.id} className="border-border/30 hover:bg-muted/20 border-b">
                      <td className="text-muted-foreground px-3 py-2">
                        {new Date(e.createdAt).toLocaleDateString(undefined, {
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="px-3 py-2 font-mono text-[0.625rem]">
                        {e.featureKey}
                        {e.kind === "batch_purchase" && (
                          <Badge variant="outline" className="ml-1 text-[0.5rem]">
                            batch
                          </Badge>
                        )}
                      </td>
                      <td className="hidden px-3 py-2 font-mono text-[0.5625rem] sm:table-cell">
                        <span className="text-muted-foreground">{e.ownerType}:</span>
                        {e.ownerId.slice(0, 8)}
                      </td>
                      <td className="px-3 py-2 text-right">{e.units}</td>
                      <td className="hidden px-3 py-2 text-right md:table-cell">
                        {euros(e.unitPriceCents)}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold">{euros(e.totalCents)}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-[0.5625rem] font-medium ${
                            STATUS_STYLES[e.status] ?? "bg-muted"
                          }`}
                        >
                          {e.status}
                        </span>
                        {e.failureReason && (
                          <div className="text-muted-foreground mt-0.5 max-w-[200px] truncate text-[0.5625rem]">
                            {e.failureReason}
                          </div>
                        )}
                      </td>
                      <td className="hidden px-3 py-2 lg:table-cell">
                        {e.stripeInvoiceItemId && (
                          <code className="text-muted-foreground text-[0.5625rem]">
                            {e.stripeInvoiceItemId.slice(0, 14)}…
                          </code>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
